import React, { useState, useRef } from 'react';
import { Task, TaskSubmission } from '../../types/task';
import { VerificationService } from '../../services/verificationService';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/components/TaskVerification.css';

interface TaskVerificationProps {
  task: Task;
  userId: string;
  onVerificationSubmitted: () => void;
  className?: string;
  existingSubmission?: TaskSubmission | null;
}

/**
 * TaskVerification - Component to handle task verification using different methods
 */
export const TaskVerification: React.FC<TaskVerificationProps> = ({
  task,
  userId,
  onVerificationSubmitted,
  className = '',
  existingSubmission = null
}) => {
  const { t } = useLanguage();
  const [emailUsername, setEmailUsername] = useState(existingSubmission?.proof?.email_username || '');
  const [textResponse, setTextResponse] = useState(existingSubmission?.proof?.text_response || '');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(existingSubmission?.proof?.screenshot_url || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if task is full or completed
  const isTaskFull = task.max_participants && task.current_participants >= task.max_participants;
  const isTaskCompleted = task.status === 'completed';
  const canSubmit = !isTaskFull && !isTaskCompleted && task.status === 'open';
  
  // Check if user's submission was rejected due to no space left
  const wasRejectedDueToSpace = existingSubmission?.status === 'rejected' && 
    existingSubmission?.feedback?.includes('maximum number of participants');

  // Handle email/username verification
  const handleEmailUsernameVerification = async () => {
    if (!canSubmit) {
      setError(isTaskFull ? t('taskVerification.taskFullError') : 
              isTaskCompleted ? t('taskVerification.taskCompletedError') : 
              t('taskVerification.taskNotAcceptingSubmissions'));
      return;
    }
    
    if (!emailUsername.trim()) {
      setError(t('taskVerification.enterEmailUsername'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submissionError } = await VerificationService.submitTaskVerification({
        task_id: task.id,
        user_id: userId,
        verification_type: 'email_username',
        proof: { email_username: emailUsername.trim() },
      });

      if (submissionError) throw submissionError;

      setSuccess(t('taskVerification.emailUsernameSubmitted'));
      onVerificationSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('taskVerification.verificationError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle text response verification
  const handleTextResponseVerification = async () => {
    if (!canSubmit) {
      setError(isTaskFull ? t('taskVerification.taskFullError') : 
              isTaskCompleted ? t('taskVerification.taskCompletedError') : 
              t('taskVerification.taskNotAcceptingSubmissions'));
      return;
    }
    
    if (!textResponse.trim()) {
      setError(t('taskVerification.provideResponse'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submissionError } = await VerificationService.submitTaskVerification({
        task_id: task.id,
        user_id: userId,
        verification_type: 'text_response',
        proof: { text_response: textResponse.trim() },
      });

      if (submissionError) throw submissionError;

      setSuccess(t('taskVerification.responseSubmitted'));
      onVerificationSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('taskVerification.verificationError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle screenshot upload
  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setScreenshot(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setScreenshotPreview(previewUrl);
    }
  };

  // Handle screenshot verification
  const handleScreenshotVerification = async () => {
    if (!canSubmit) {
      setError(isTaskFull ? t('taskVerification.taskFullError') : 
              isTaskCompleted ? t('taskVerification.taskCompletedError') : 
              t('taskVerification.taskNotAcceptingSubmissions'));
      return;
    }
    
    if (!screenshot) {
      setError(t('taskVerification.uploadScreenshot'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload screenshot
      const { url, error: uploadError } = await VerificationService.uploadScreenshot(
        task.id,
        userId,
        screenshot
      );

      if (uploadError) throw uploadError;
      if (!url) throw new Error(t('taskVerification.screenshotUploadFailed'));

      // Submit verification
      const { error: submissionError } = await VerificationService.submitTaskVerification({
        task_id: task.id,
        user_id: userId,
        verification_type: 'screenshot',
        proof: { screenshot_url: url },
      });

      if (submissionError) throw submissionError;

      setSuccess(t('taskVerification.screenshotSubmitted'));
      onVerificationSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('taskVerification.verificationError'));
    } finally {
      setLoading(false);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`task-verification-section ${className}`}>
      <h4>{t('taskVerification.verifyTaskCompletion')}</h4>
      
      {/* Task instructions */}
      <div className="verification-instructions">
        <strong>{t('taskVerification.instructions')}:</strong> {task.instructions}
      </div>
      
      {/* Error and success messages */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}
      
      {/* Warning message when task is full or completed */}
      {(!canSubmit && (isTaskFull || isTaskCompleted)) && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {isTaskFull ? 
            t('taskVerification.taskFullWarning') :
            t('taskVerification.taskCompletedWarning')
          }
        </div>
      )}
      
      {/* Special message for users whose submission was rejected due to no space */}
      {wasRejectedDueToSpace && (
        <div className="alert alert-info" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          {t('taskVerification.rejectedDueToSpace')}
        </div>
      )}
      
      {/* Verification method specific UI */}
      {task.verification_type === 'email_username' && (
        <div className="verification-method">
          <div className="verification-form-group">
            <label htmlFor="emailUsername">
              📧 {t('taskVerification.enterEmailUsernameLabel')}:
            </label>
            <input
              type="text"
              id="emailUsername"
              className="form-control verification-input"
              value={emailUsername}
              onChange={(e) => setEmailUsername(e.target.value)}
              placeholder={t('taskVerification.emailUsernamePlaceholder')}
              disabled={loading || !canSubmit}
            />
          </div>
          <button
            className={`verification-submit-btn ${loading ? 'btn-loading' : ''}`}
            onClick={handleEmailUsernameVerification}
            disabled={loading || !emailUsername.trim() || !canSubmit}
          >
            {loading ? t('taskVerification.submitting') : `📤 ${t('taskVerification.submitEmailUsername')}`}
          </button>
        </div>
      )}
      
      {task.verification_type === 'text_response' && (
        <div className="verification-method">
          <div className="verification-form-group">
            <label htmlFor="textResponse">
              💬 {task.verification_details.verification_question ? 
                `${task.verification_details.verification_question}` : 
                t('taskVerification.provideRequestedInfo')}
            </label>
            <textarea
              id="textResponse"
              className="form-control verification-input"
              value={textResponse}
              onChange={(e) => setTextResponse(e.target.value)}
              placeholder={t('taskVerification.responsePlaceholder')}
              rows={4}
              disabled={loading || !canSubmit}
            />
          </div>
          <button
            className={`verification-submit-btn ${loading ? 'btn-loading' : ''}`}
            onClick={handleTextResponseVerification}
            disabled={loading || !textResponse.trim() || !canSubmit}
          >
            {loading ? t('taskVerification.submitting') : `📝 ${t('taskVerification.submitResponse')}`}
          </button>
        </div>
      )}
      
      {task.verification_type === 'screenshot' && (
        <div className="verification-method">
          <p>{t('taskVerification.uploadScreenshotProof')}:</p>
          
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleScreenshotChange}
            className="upload-input"
            disabled={loading || !canSubmit}
          />
          
          {/* Enhanced upload area with modern design */}
          <div 
            className={`upload-area ${!canSubmit ? 'disabled' : ''}`} 
            onClick={canSubmit ? handleUploadClick : undefined}
          >
            <div className="upload-area-content">
              <p className="upload-label">
                {screenshot ? t('taskVerification.replaceScreenshot') : t('taskVerification.uploadScreenshotProof')}
              </p>
              <button 
                type="button" 
                className="upload-button" 
                disabled={!canSubmit}
              >
                {screenshot ? `📷 ${t('taskVerification.changeFile')}` : `📁 ${t('taskVerification.chooseFile')}`}
              </button>
              {!screenshot && (
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  {t('taskVerification.dragDropOrBrowse')}
                </p>
              )}
            </div>
          </div>

          {/* File info display */}
          {screenshot && (
            <div className="file-info">
              <div className="file-info-details">
                <span className="file-info-name">{screenshot.name}</span>
                <span className="file-info-size">
                  {(screenshot.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <button 
                type="button" 
                className="file-remove-btn"
                onClick={() => {
                  setScreenshot(null);
                  setScreenshotPreview(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                title={t('taskVerification.removeFile')}
              >
                ✕
              </button>
            </div>
          )}
          
          {/* Preview of uploaded screenshot */}
          {screenshotPreview && (
            <div className="text-center" style={{ marginTop: '1rem' }}>
              <img
                src={screenshotPreview}
                alt={t('taskVerification.screenshotPreview')}
                className="verification-preview"
                style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
              />
            </div>
          )}
          
          <button
            className={`verification-submit-btn ${loading ? 'btn-loading' : ''}`}
            onClick={handleScreenshotVerification}
            disabled={loading || !screenshot || !canSubmit}
          >
            {loading ? t('taskVerification.uploading') : `🚀 ${t('taskVerification.submitScreenshot')}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskVerification;
